"use client";
import clsx from "clsx";
import { useEffect, useState, ChangeEvent, KeyboardEvent } from 'react';
import { avenirNextRegular, avenirNextMedium } from '@/styles/fonts'
import { useRouter } from 'next/navigation'
import { debounce } from "lodash";
import HomeSearchResults from '@/app/components/homesearchresults.client';
import Link from "next/link";
import Image from "next/image";

// Logo imports
import capitalOneLogo from '@/app/images/logos/logo-white-capitalone.png';
import intelLogo from '@/app/images/logos/logo-white-intel.png';
import outbrainLogo from '@/app/images/logos/logo-white-outbrain.png';
import publicisLogo from '@/app/images/logos/logo-white-publicis.png';

interface Advertiser {
  profileUrl: string;
  advertiser: string;
  advertiserName: string;
}

const initialSearchResults: {
  advertisers: Advertiser[]
} = {
  advertisers: [],
};



export default function HomeSearch() {
  const router = useRouter();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<typeof initialSearchResults>({
    ...initialSearchResults
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasResults, setHasResults] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const search = async () => {
      setSearchResults(initialSearchResults);
      setHasResults(false);
      if (searchTerm) {
        const response = await fetch(`/free-ui-api/auto-search?q=${searchTerm}`, { signal });
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        setSearchResults(data);
        setHasResults(data.advertisers.length || data.publishers.length > 0);
      } else {
        setSearchResults({ ...initialSearchResults });
        setHasResults(false);
      }
    }

    const debouncedSearch = debounce(search, 300);

    debouncedSearch();

    return () => {
      debouncedSearch.cancel();
    }
  }, [searchTerm]);

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }

  const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchResults.advertisers.length) {
        router.push(searchResults.advertisers[activeIndex].profileUrl);
      }
    } else if (e.key === 'Escape') {
      setSearchTerm('');
      setActiveIndex(0);
    } else if (e.key === 'ArrowDown') {
      setActiveIndex((prev) => (prev + 1) % searchResults.advertisers.length)
    } else if (e.key === 'ArrowUp') {
      setActiveIndex((prev) => (prev - 1) % searchResults.advertisers.length)
    }
  }

  return (
    <div className="z-0 flex flex-col w-full items-center md:h-[400px]
   lg:w-[970px] xl:w-[1170px] 
    ">
      <h1 className={clsx(avenirNextRegular.className,
        "text-center text-white",
        "leading-[1.1]",
        "text-[21px] sm:text-[35px] md:text-[45px] lg:text-[48px]",
        "lg:pt-[50px]",
        "px-[10px] sm:px-0",
        "md:w-[75%] lg:w-full",
        "md:leading-[50px]")}>
        Spy on Your Competitor&apos;s Ad Strategies
      </h1>
      <div className={clsx(
        "text-[#43484d] relative px-[15px] mb-[10px]",
        "w-full md:w-[60%] lg:w-[60%] xl:w-[52%]",
        "mt-[5px] sm:mt-[15px] md:mt-[23px] lg:mt-[30px]"
      )}>

        <input
          value={searchTerm} onChange={onSearchChange} onKeyDown={onSearchKeyDown}
          suppressHydrationWarning={true} data-lpignore="true" autoComplete="off"
          className={clsx(avenirNextMedium.className,
            "w-full focus:outline-none bg-white shadow-sm",
            "border-[8px] border-[#414e85] rounded-[5px]",
            "px-[25px] md:px-[30px]",
            "py-[10px] sm:py-[15px] md:py-[24px] lg:py-[30px]",
            "text-[13px] sm:text-[20px] md:text-[22px]"
          )}
          placeholder="Enter an advertiser domain" />
        <span
          className={clsx(
            "text-[#b3b4bb] pointer-events-none absolute",
            "w-[14px] sm:w-[24px]",
            "h-[14px] sm:h-[24px]",
            "right-[50px] sm:right-[60px] md:right-[80px]",
            "top-[20px] sm:top-[25px] md:top-[35px] lg:top-[45px]"
          )}>
          <svg fill="currentColor" viewBox="0 0 16 16" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" >
            <path d="M12.027 9.92L16 13.95 14 16l-4.075-3.976A6.465 6.465 0 0 1 6.5 13C2.91 13 0 10.083 0 6.5 0 2.91 2.917 0 6.5 0 10.09 0 13 2.917 13 6.5a6.463 6.463 0 0 1-.973 3.42zM1.997 6.452c0 2.48 2.014 4.5 4.5 4.5 2.48 0 4.5-2.015 4.5-4.5 0-2.48-2.015-4.5-4.5-4.5-2.48 0-4.5 2.014-4.5 4.5z" fillRule="evenodd" />
          </svg>
        </span>
        {hasResults &&
          <HomeSearchResults {...searchResults}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex} />}

      </div>

      <div className="text-[16px] pt-[4px] flex flex-row flex-wrap justify-center">
        <span className="text-[#86939e] mr-[5px]">Popular Searches:</span>
        <Link className="mr-[5px] text-[#2ea2f8] hover:text-[#23527c] hover:underline"
          href="/free/advertiser/fidelity-investments/fidelity.com">fidelity.com,</Link>
        <Link className="mr-[5px] text-[#2ea2f8] hover:text-[#23527c] hover:underline"
          href="/free/advertiser/Squarespace/squarespace.com">squarespace.com,</Link>
        <Link className="text-[#2ea2f8] hover:text-[#23527c] hover:underline"
          href="/free/advertiser/geico/geico.com">geico.com</Link>
      </div>

      <Link
        href={searchResults.advertisers.length ? searchResults.advertisers[0].profileUrl : "/"}
        className={clsx(avenirNextMedium.className,
          "text-[18px] rounded-md bg-[#e46700] px-[60px] py-[16px] text-white hover:text-black",
          "mb-[10px]",
          "mt-[45px]")}
      >
        Adbeat Search
      </Link>

      <div>

        <div className="pt-[41px] pb-[15px] text-center text-white text-[20px] leading-[1.1]">
          <h2>Trusted by the World&apos;s Leading Companies</h2>
        </div>
        <div className="flex flex-row flex-wrap justify-center w-full">
          <Image className="mx-[5px] sm:mx-[5px]" src={capitalOneLogo} width={153} height={50} alt="capitalone" />
          <Image className="mx-[5px] sm:mx-[5px]" src={intelLogo} width={101} height={50} alt="intel" />
          <Image className="mx-[5px] sm:mx-[5px]" src={outbrainLogo} width={190} height={50} alt="outbrain" />
          <Image className="mx-[5px] sm:mx-[5px]" src={publicisLogo} width={186} height={50} alt="publicis" />
        </div>
      </div>

    </div>)

}

// export without ssr to prevent problems with LastPass; however it adds layout shift on load
// https://nextjs.org/docs/pages/building-your-application/optimizing/lazy-loading#with-no-ssr
// export default dynamic(() => Promise.resolve(HomeSearchInputNoSSR), { ssr: false });

// instead I added suppressHydrationWarning to input box
// https://nextjs.org/docs/messages/react-hydration-error#solution-3-using-suppresshydrationwarning
